import 'react-toastify/dist/ReactToastify.css';
import './App.scss';
import './Intercom.config';
import { RouterProvider } from 'react-router-dom';
import { AppState, Auth0Provider } from '@auth0/auth0-react';
import { ToastContainer } from 'react-toastify';
import { getEnvVariables } from './env';
import { UserActivityTracker } from '@components/UserActivityTracker/UserActivityTracker';
import { router } from '@router/router';
import { NoConnection } from '@dynamika-shared/ui/icons';
import { Provider } from 'react-redux';
import { store } from '@restore/store';
import { WithAuthorizationHoc } from '@components/HOC/WithAuthorization/WithAuthorization.hoc';
import { runToast } from '@restore/tools';
import { keyboardManager, logger } from '@shared/services/AdvancedLogger';
import { WithRoleModules } from '@components/HOC/WithRoleModules/WithRoleModules';
import { NavbarProvider } from '@components/Navbar/NavbarContext';
import { DevFloatingPanel } from '@components/DevFloatingPanel';
import { MaintenancePage } from 'src/pages/Maintenance/Maintenance';
import { getFeatureFlagValue } from '@dynamika-shared/services/featureFlags';
import { RETURN_TO_KEY } from '@router/roleAccess';
import { buildAuthorizationParams, resolveAuthFlow } from '@router/authFlow';

keyboardManager.enable();

function handleRedirectCallback(appState?: AppState) {
  const returnTo = (appState as { returnTo?: string } | undefined)?.returnTo;
  if (returnTo) {
    sessionStorage.setItem(RETURN_TO_KEY, returnTo);
  }
}

let errorTimeout: NodeJS.Timeout;

function handleConnectionChange() {
  if (navigator.onLine) {
    console.timeEnd('errorTimeout');
    if (errorTimeout) {
      clearTimeout(errorTimeout);
    } else {
      runToast('success', 'Internet connection restored', '');
    }
  } else {
    console.time('errorTimeout');
    errorTimeout = setTimeout(() => {
      runToast(
        'warning',
        'Please check your internet connection and try again.',
        'Network connection error',
        {
          autoClose: 10000,
          icon: <NoConnection />,
        }
      );
    }, 1000);
  }
}

window.addEventListener('online', handleConnectionChange);
window.addEventListener('offline', handleConnectionChange);
window.addEventListener('unload', () => {
  logger.info('location', window.location, 'BUG: D7-4889', [
    'bug',
    'navigation',
  ]);
});

const MNT_BYPASS_KEY = 'mnt_bypass';

function resolveMaintenanceBypass(): boolean {
  const urlVal = new URLSearchParams(window.location.search).get(
    MNT_BYPASS_KEY
  );
  if (urlVal !== null) {
    if (urlVal === '1' || urlVal === 'true') {
      localStorage.setItem(MNT_BYPASS_KEY, '1');
    } else {
      localStorage.removeItem(MNT_BYPASS_KEY);
    }
  }
  return localStorage.getItem(MNT_BYPASS_KEY) === '1';
}

function App() {
  if (getFeatureFlagValue('MNT') && !resolveMaintenanceBypass()) {
    return <MaintenancePage />;
  }

  const { AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_CONNECTION_TYPE } =
    getEnvVariables();

  const flow = resolveAuthFlow(window.location);
  const authorizationParams = buildAuthorizationParams(
    flow,
    AUTH0_CONNECTION_TYPE
  );

  return (
    <Provider store={store}>
      <Auth0Provider
        domain={AUTH0_DOMAIN}
        clientId={AUTH0_CLIENT_ID}
        cacheLocation='localstorage'
        useRefreshTokens={true}
        authorizationParams={authorizationParams}
        onRedirectCallback={handleRedirectCallback}
      >
        <WithAuthorizationHoc>
          <WithRoleModules>
            <NavbarProvider>
              <UserActivityTracker />
              <RouterProvider router={router} />
              <ToastContainer />
              <DevFloatingPanel />
            </NavbarProvider>
          </WithRoleModules>
        </WithAuthorizationHoc>
      </Auth0Provider>
    </Provider>
  );
}

export default App;
